import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';

import useQuery from '../../utils/useQuery';
import { validateEmailWithLocalization, validateLengthWithLocalization } from '../../utils/validation';
import { sendSignInRequest } from '../../utils/api/accountApi';
import { saveTokens } from '../../utils/auth';
import { genericErrorMessage } from '../../utils/lang/commonErrors';
import { isApiError } from '../../utils/api/clearApiClient';

import SubmitButton from './fields/SubmitButton';
import PasswordInput from './fields/PasswordInput';
import EmailInput from './fields/EmailInput';
import ReCaptchaDisclaimer from './fields/reCaptchaDisclaimer';

import styles from './LoginForm.scss';

const LoginForm = () => {
  const { email, access_token: accessToken, refresh_token: refreshToken } = useQuery();
  const history = useHistory();

  // displays loader in the SubmitButton if true
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (accessToken && refreshToken) {
      setLoading(true);
      saveTokens(accessToken, refreshToken);
      history.push('/profile');
    }
  }, []);

  const [emailValue, setEmailValue] = useState(email || '');
  const onEmailChange = (e) => setEmailValue(e.target.value);

  const [passValue, setPassValue] = useState('');
  const onPassChange = (e) => setPassValue(e.target.value);

  const [formErrors, setFormErrors] = useState({});

  const intl = useIntl();

  const onSubmit = () => {
    setLoading(true);
    const emailError = validateEmailWithLocalization(intl, emailValue);
    const passError = validateLengthWithLocalization(intl, passValue, 8, 21);

    if (!emailError && !passError) {
      sendSignInRequest(emailValue, passValue)
        .then(() => {
          history.push('/profile');
        })
        .catch((err) => {
          if (isApiError(err) && err.response.data.code === 3 && err.response.data.message === 'AccountDeleted') {
            window.location = '/enter/restore';
          } else if (isApiError(err) && err.response.data.code === 3) {
            setFormErrors({
              pass: intl.formatMessage({
                id: 'login.form.error.invalidCredentials',
                defaultMessage: 'Email or password is incorrect',
                description: 'Error message show to the user after invalid credentials submit',
              }),
            });
          } else {
            setFormErrors({
              pass: intl.formatMessage(genericErrorMessage),
            });
          }
          setLoading(false);
        });
    } else {
      setFormErrors({
        email: emailError,
        pass: passError,
      });
      setLoading(false);
    }
  };

  return (
    <form className={styles.form}>
      <h2 className={styles.title}>
        <FormattedMessage id="login.title" defaultMessage="Log in" description="Title of the login page" />
      </h2>
      <p className={styles.subtitle}>
        <FormattedMessage
          id="login.subtitle"
          defaultMessage="If you don’t have an account yet, please, <link>sign up</link>"
          description="Subtitle of the login page, offering user to sign up if they don't have an account yet"
          values={{
            link: (chunk) => <Link to="/enter">{chunk}</Link>,
          }}
        />
      </p>

      <EmailInput value={emailValue} onChange={onEmailChange} error={formErrors.email} />

      <PasswordInput
        passValue={passValue}
        onChange={onPassChange}
        emailValue={emailValue}
        error={formErrors.pass}
        showForgotPassword
      />

      <SubmitButton
        text={intl.formatMessage({
          id: 'login.form.cta',
          defaultMessage: 'Log In',
          description: 'Submit button of the login form',
        })}
        onSubmit={onSubmit}
        isLoading={isLoading}
        className={styles.cta}
      />

      <ReCaptchaDisclaimer />
    </form>
  );
};

export default LoginForm;
